import { useCallback, useEffect, useState, VFC } from 'react';
import TextField from '@material-ui/core/TextField';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useNameComponent } from 'hooks';
import { Spacer } from '../../../shared';
import {
  rutCleaner,
  validateIdentifier,
} from '../../../../src/helpers/validations/businessIdentifier';
import { useTranslation } from '../../../i18n/infra/useTranslation';
import { COUNTRY_CODE_CL } from '../../../../src/helpers/constants';
import { UnifiedRegisterData } from '../../domain/entity/UnifiedRegisterData';
import { getNameByIdentifier } from '../../../../src/store/actions/businessActions';

interface BussinessInfoProps {
  countryId: string;
  hookForm: UseFormReturn<UnifiedRegisterData, object>;
  dataCy?: string;
}

const BussinessInfo: VFC<BussinessInfoProps> = function BussinessInfo({
  countryId,
  hookForm,
  dataCy,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { trigger, setValue } = hookForm;

  const [inputBusinessNameDisabled, setBusinessNameDisabled] = useState(false);
  const [businessNameValid, setBusinessNameValid] = useState(false);
  const [businessName, setBusinessName] = useState('');

  const { nameByIdentifier } = useSelector(
    (state: { businessState: any }) => state.businessState
  );

  // eslint-disable-next-line no-unused-vars
  let formatter: (_: string) => string;
  switch (countryId) {
    case COUNTRY_CODE_CL:
      formatter = rutCleaner;
      break;
    default:
      formatter = (input: string) => input;
      break;
  }

  const changeIdentifier = useCallback(
    (identifier: string) => {
      const formattedIdentifier = formatter(identifier);
      setValue('identifier', formattedIdentifier);
      if (validateIdentifier(formattedIdentifier, countryId)) {
        dispatch(getNameByIdentifier(formattedIdentifier, countryId));
        setBusinessNameValid(true);
      } else {
        setBusinessNameValid(false);
      }
      trigger(['businessName', 'identifier']);
    },
    [countryId, dispatch, formatter, setValue, trigger]
  );

  const identifierRules = {
    required: t('REQUIRED_IDENTIFIER'),
    validate: (identifier: string) => validateIdentifier(identifier, countryId),
  };
  const businessNameRules = {
    required: t('REQUIRED_BUSINESS_NAME'),
    pattern: {
      value: /^[A-Za-z\u00C0-\u017F0-9 .,_&-]+$/,
      message: t('ERROR_BUSINESS_NAME'),
    },
  };
  const changeBusinessName = useCallback(
    (name: string) => {
      setValue('businessName', name);
      setBusinessName(name);
      trigger(['businessName', 'identifier']);
    },
    [setValue, trigger]
  );

  const regexNameValidator = (name: string) => {
    return /^[A-Za-z\u00C0-\u017F0-9 .,_&-]+$/.test(name);
  };

  useEffect(() => {
    if (nameByIdentifier && businessNameValid) {
      setBusinessNameDisabled(regexNameValidator(nameByIdentifier));
      changeBusinessName(nameByIdentifier);
    } else {
      setBusinessNameDisabled(false);
    }
  }, [changeBusinessName, nameByIdentifier, businessNameValid]);

  return (
    <>
      <Controller
        name="identifier"
        rules={identifierRules}
        control={hookForm.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value || ''}
            variant="outlined"
            placeholder={t('PLACEHOLDER_TAX_IDENTIFIER')}
            label={t('LABEL_TAX_IDENTIFIER')}
            onChange={e => {
              changeIdentifier(e.target.value);
            }}
            type="text"
            fullWidth
            error={!!fieldState.error}
            helperText={
              fieldState.error?.message ||
              (fieldState.error?.type === 'validate'
                ? t('ERROR_INVALID_IDENTIFIER')
                : '')
            }
            InputProps={{
              inputProps: {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                'data-cy': useNameComponent(dataCy, 'identifier'),
              },
            }}
          />
        )}
      />
      <Spacer spacing={2} />
      <Controller
        name="businessName"
        rules={businessNameRules}
        control={hookForm.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value || ''}
            variant="outlined"
            placeholder={t('PLACEHOLDER_BUSINESS_NAME')}
            label={t('LABEL_BUSINESS_NAME')}
            onChange={e => {
              changeBusinessName(e.target.value);
            }}
            disabled={inputBusinessNameDisabled}
            type="text"
            fullWidth
            error={!!fieldState.error}
            helperText={
              businessName.length > 0 && inputBusinessNameDisabled
                ? t('NAME_BY_IDENTIFIER_SUCCESS')
                : fieldState.error?.message
            }
            InputProps={{
              inputProps: {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                'data-cy': useNameComponent(dataCy, 'businessName'),
              },
            }}
          />
        )}
      />
    </>
  );
};

export default BussinessInfo;
